import React, { useEffect, useMemo, useRef, useState } from 'react';
import { canEditMissionBoard, hasCommandFeature, COMMAND_FEATURE_PERMS } from '../utils/permissionControl';
import ReactDOM from 'react-dom';
import { getMemberExpandKey } from '../utils/dashboard/memberTreeHelpers';
import { getMemberDisplayName, missionIdMatchesMember, looksLikeGhostMissionId, getCanonicalMemberId } from '../utils/dashboard/deviceIdentity';
import { getMemberListDisplayName, memberNoteKey } from '../utils/dashboard/commanderMemberNoteHelpers';
import MemberNoteEditorModal from './MemberNoteEditorModal';
import { PRESENCE_STATUS, PRESENCE_DOT_COLORS } from '../utils/dashboard/constants';
import MissionMemberChatWindow, { VoiceIconButton } from './MissionMemberChatWindow';
import MemberCollapsedCard, {
  FreqStatusBadge,
  TrackSwitch,
  formatCollapsedSeenText
} from './MemberCollapsedCard';
import { message } from 'antd';

const COLORS = ['#1976d2', '#2e7d32', '#ed6c02', '#9c27b0', '#00838f', '#c62828'];

function memberIdOf(m) {
  return getMemberExpandKey(m);
}

function presenceOf(member, getPresence) {
  if (typeof getPresence === 'function') {
    const s = getPresence(member);
    if (s) return s;
  }
  return PRESENCE_STATUS.OFFLINE;
}

function presenceDotColor(status) {
  if (status === PRESENCE_STATUS.ONLINE || status === 'online') return PRESENCE_DOT_COLORS.online || '#2ea8ff';
  if (status === PRESENCE_STATUS.TEMP_OFFLINE || status === 'temp_offline') return PRESENCE_DOT_COLORS.temp_offline || '#f59e0b';
  return PRESENCE_DOT_COLORS.offline || '#8a93a0';
}

function presenceLabel(status) {
  if (status === PRESENCE_STATUS.ONLINE || status === 'online') return '在线';
  if (status === PRESENCE_STATUS.TEMP_OFFLINE || status === 'temp_offline') return '暂时离开';
  return '离线';
}

function formatMemberSeen(lastSeen, offline = false) {
  return formatCollapsedSeenText(lastSeen, { offline });
}

function formatFullSeen(lastSeen) {
  if (!lastSeen) return null;
  const d = new Date(lastSeen);
  if (Number.isNaN(d.getTime())) return null;
  const pad = (n) => String(n).padStart(2, '0');
  return `更新于 ${d.getFullYear()}/${pad(d.getMonth() + 1)}/${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function fmtMetric(v, unit = '', digits = null) {
  if (v == null || v === '') return '未知';
  const n = Number(v);
  if (!Number.isFinite(n)) return '未知';
  const text = digits != null ? n.toFixed(digits) : String(n);
  return `${text}${unit}`;
}

function buildMemberInfoLines(member, status, notesByKey = null) {
  const name = getMemberListDisplayName(member, notesByKey) || memberIdOf(member) || '未知';
  const batteryText = member?.battery_level != null ? `电量 ${member.battery_level}%` : null;
  const seenText = formatMemberSeen(member?.last_seen);
  return [
    name,
    presenceLabel(status),
    batteryText,
    seenText,
    member?.org_name ? `组织 ${member.org_name}` : null,
    member?.group_name ? `小组 ${member.group_name}` : null,
    member?.device_unique_id ? `设备 ${member.device_unique_id}` : null,
    member?.name && member.name !== name ? `标识 ${member.name}` : null
  ].filter(Boolean);
}


/** 小号拖拽预览：避免浏览器默认拖影因祖先层合成而克隆整块侧栏面板 */
function createMissionNameDragImage(label, status) {
  const ghost = document.createElement('div');
  ghost.setAttribute('data-mission-drag-ghost', '1');
  Object.assign(ghost.style, {
    position: 'fixed',
    top: '-1000px',
    left: '-1000px',
    zIndex: '99999',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '5px',
    height: '26px',
    maxWidth: '160px',
    padding: '0 8px',
    borderRadius: '6px',
    border: '1px solid #e6ebf0',
    background: '#fff',
    boxShadow: '0 4px 12px rgba(0,0,0,0.18)',
    fontSize: '12px',
    fontWeight: '600',
    color: '#222',
    fontFamily: 'inherit',
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden'
  });
  const dot = document.createElement('span');
  Object.assign(dot.style, {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    background: presenceDotColor(status),
    flexShrink: '0'
  });
  const text = document.createElement('span');
  text.textContent = label || '队员';
  Object.assign(text.style, {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    maxWidth: '120px'
  });
  ghost.appendChild(dot);
  ghost.appendChild(text);
  document.body.appendChild(ghost);
  return ghost;
}

function attachMissionNameDragImage(dataTransfer, label, status) {
  if (!dataTransfer || typeof dataTransfer.setDragImage !== 'function') return;
  try {
    const ghost = createMissionNameDragImage(label, status);
    dataTransfer.setDragImage(ghost, 12, 13);
    requestAnimationFrame(() => {
      setTimeout(() => {
        try { ghost.remove(); } catch (_) { /* ignore */ }
      }, 0);
    });
  } catch (_) { /* ignore */ }
}


function stubMemberFromId(id) {
  if (!id) return null;
  const parts = String(id).split(':');
  const short = parts[parts.length - 1] || id;
  return {
    name: short,
    device_unique_id: id,
    __missionStub: true
  };
}

function NameChip({
  member,
  status = PRESENCE_STATUS.OFFLINE,
  draggable = true,
  onDragStart,
  onRemove,
  showVoice = false,
  voicePulsing = false,
  onVoiceClick,
  onFlyTo = null,
  compact = false,
  notesByKey = null,
  onOpenCommandMenu = null
}) {
  const [hover, setHover] = useState(false);
  const name = getMemberListDisplayName(member, notesByKey) || memberIdOf(member) || '未知';
  const infoLines = buildMemberInfoLines(member, status, notesByKey);
  const tipRef = useRef(null);

  return (
    <div
      draggable={draggable}
      onDragStart={(e) => {
        setHover(false);
        onDragStart?.(e);
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        position: 'relative',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        height: compact ? 26 : 28,
        maxWidth: '100%',
        padding: '0 8px',
        borderRadius: 6,
        border: '1px solid #e6ebf0',
        background: '#fff',
        cursor: draggable ? 'grab' : 'default',
        fontSize: 12,
        color: '#333',
        userSelect: 'none',
        verticalAlign: 'top'
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: presenceDotColor(status),
          flexShrink: 0
        }}
        title={presenceLabel(status)}
      />
      <span
        role={(onOpenCommandMenu || onFlyTo) ? 'button' : undefined}
        title={onOpenCommandMenu ? '点击打开菜单' : (onFlyTo ? '点击定位到地图' : undefined)}
        onClick={(e) => {
          e.stopPropagation();
          if (onOpenCommandMenu) {
            onOpenCommandMenu(member, e.currentTarget);
            return;
          }
          if (!onFlyTo) return;
          onFlyTo(member);
        }}
        style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          fontWeight: 600,
          color: '#222',
          maxWidth: 120,
          cursor: (onOpenCommandMenu || onFlyTo) ? 'pointer' : 'inherit'
        }}
      >
        {name}
      </span>
      {showVoice ? (
        <VoiceIconButton
          unread={voicePulsing}
          onClick={() => onVoiceClick?.(member)}
        />
      ) : null}
      {status !== PRESENCE_STATUS.ONLINE && status !== 'online' ? (
        <span style={{ fontSize: 10, color: '#8a93a0', flexShrink: 0 }}>
          {presenceLabel(status)}
        </span>
      ) : null}
      {onRemove ? (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onRemove(); }}
          style={{
            border: 'none',
            background: 'transparent',
            color: '#999',
            cursor: 'pointer',
            fontSize: 14,
            lineHeight: 1,
            padding: 0,
            flexShrink: 0
          }}
          title="移出任务"
        >
          ×
        </button>
      ) : null}
      {hover ? (
        <div
          ref={tipRef}
          style={{
            position: 'absolute',
            left: 0,
            bottom: '100%',
            marginBottom: 4,
            zIndex: 40,
            minWidth: 180,
            maxWidth: 280,
            padding: '8px 10px',
            background: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 8,
            boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
            fontSize: 11,
            lineHeight: 1.55,
            color: '#333',
            whiteSpace: 'pre-line',
            pointerEvents: 'none'
          }}
        >
          {infoLines.join('\n')}
        </div>
      ) : null}
    </div>
  );
}

function signalLabel(gsm) {
  if (gsm == null || gsm === '') return { text: '未知', color: '#9ca3af', bars: 0 };
  const raw = String(gsm);
  const n = Number(gsm);
  if (/良好|强|优|good|excellent/i.test(raw)) return { text: '良好', color: '#16a34a', bars: 4 };
  if (/一般|中|fair|mid/i.test(raw)) return { text: '一般', color: '#ca8a04', bars: 3 };
  if (/弱|差|poor|weak/i.test(raw)) return { text: '较弱', color: '#ea580c', bars: 2 };
  if (Number.isFinite(n)) {
    if (n >= 3 || n >= 70) return { text: '良好', color: '#16a34a', bars: 4 };
    if (n >= 2 || n >= 40) return { text: '一般', color: '#ca8a04', bars: 3 };
    return { text: '较弱', color: '#ea580c', bars: 2 };
  }
  return { text: raw, color: '#374151', bars: 3 };
}

function SignalBars({ bars = 0, color = '#16a34a' }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'flex-end', gap: 1, height: 12, marginRight: 4 }}>
      {[1, 2, 3, 4].map((i) => (
        <span
          key={i}
          style={{
            width: 3,
            height: 3 + i * 2,
            borderRadius: 1,
            background: i <= bars ? color : '#e5e7eb'
          }}
        />
      ))}
    </span>
  );
}

function BatteryIcon({ pct }) {
  const n = Number(pct);
  const ok = Number.isFinite(n);
  const fill = !ok ? '#9ca3af' : (n > 50 ? '#16a34a' : (n > 20 ? '#ca8a04' : '#dc2626'));
  const w = ok ? Math.max(1, Math.min(10, Math.round((Math.max(0, Math.min(100, n)) / 100) * 10))) : 0;
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <svg width="18" height="10" viewBox="0 0 18 10" aria-hidden="true">
        <rect x="0.5" y="1.5" width="14" height="7" rx="1.5" stroke={fill} fill="none" strokeWidth="1.2" />
        <rect x="15.2" y="3.2" width="2" height="3.6" rx="0.6" fill={fill} />
        <rect x="2" y="3" width={w} height="4" rx="0.5" fill={fill} />
      </svg>
      <span style={{ fontWeight: 700, color: '#111827' }}>{ok ? `${n}%` : '未知'}</span>
    </span>
  );
}

function resolveDeviceBrandModel(member) {
  const brand = String(
    member?.device_brand || member?.brand || member?.manufacturer || member?.phone_brand || ''
  ).trim();
  const model = String(
    member?.device_model || member?.model || member?.phone_model || member?.phoneModel || ''
  ).trim();
  if (brand && model) {
    if (model.toLowerCase().includes(brand.toLowerCase())) return model;
    return brand + ' ' + model;
  }
  return brand || model || '';
}

function GroupMemberDetail({
  member,
  status,
  onLocate = null,
  onSendCommand = null,
  trackOn = false,
  onTrackToggle = null,
  onTrackMenu = null,
  onCopyCoords = null,
  capability = null,
  onCapabilityChange = null,
  onOpenCommandMenu = null
}) {
  const [trackOpen, setTrackOpen] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const boxRef = useRef(null);
  const online = status === PRESENCE_STATUS.ONLINE || status === 'online';
  const presenceText = presenceLabel(status);
  const sig = signalLabel(member?.gsm);
  const actionBtn = {
    height: 28,
    padding: '0 10px',
    borderRadius: 6,
    border: '1px solid #d1d5db',
    background: '#fff',
    color: '#374151',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4
  };

  useEffect(() => {
    if (!trackOpen && !moreOpen) return undefined;
    const onDown = (e) => {
      if (boxRef.current?.contains?.(e.target)) return;
      setTrackOpen(false);
      setMoreOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    return () => document.removeEventListener('mousedown', onDown, true);
  }, [trackOpen, moreOpen]);

  const cell = (label, valueNode) => (
    <div className="mission-detail-cell" style={{ minWidth: 0, display: 'flex', gap: 6, alignItems: 'baseline', lineHeight: 1.55 }}>
      <span style={{ color: '#9ca3af', flexShrink: 0, minWidth: 28 }}>{label}</span>
      <span style={{ color: '#111827', fontWeight: 600, minWidth: 0, wordBreak: 'break-all' }}>{valueNode}</span>
    </div>
  );

  return (
    <div
      ref={boxRef} className="mission-member-detail"
      style={{
        padding: '10px 12px',
        background: '#fff',
        fontSize: 12,
        borderTop: '1px solid #e5e7eb'
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10,
        paddingBottom: 8,
        borderBottom: '1px solid #f3f4f6'
      }}
      >
        <div>
          <span style={{ color: '#9ca3af' }}>状态 </span>
          <span style={{ fontWeight: 700, color: online ? '#16a34a' : '#6b7280' }}>{presenceText}</span>
        </div>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          <span style={{ color: '#9ca3af' }}>电量</span>
          <BatteryIcon pct={member?.battery_level} />
        </div>
      </div>

      <div className="mission-detail-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px', marginBottom: 10 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div className="mission-detail-section-title">位置与运动</div>
          {cell('经度', member?.last_longitude != null ? Number(member.last_longitude).toFixed(6) : '未知')}
          {cell('纬度', member?.last_latitude != null ? Number(member.last_latitude).toFixed(6) : '未知')}
          {cell('海拔', member?.last_altitude != null ? `${member.last_altitude} m` : '未知')}
          {cell('精度', fmtMetric(member?.last_accuracy, ' m'))}
          {cell('速度', fmtMetric(member?.last_speed, ' m/s', 1))}
          {cell('方向', fmtMetric(member?.last_bearing, '°', 1))}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <div className="mission-detail-section-title">设备与归属</div>
          {cell('信号', (
            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
              <SignalBars bars={sig.bars} color={sig.color} />
              <span style={{ color: sig.color }}>{sig.text}</span>
            </span>
          ))}
          {cell('组织', member?.org_name || '—')}
          {cell('小组', member?.group_name || '—')}
        </div>
      </div>

      {(() => {
        const deviceLabel = resolveDeviceBrandModel(member);
        if (!deviceLabel) return null;
        return (
          <div
            className="mission-detail-device"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 6,
              marginBottom: 12,
              fontSize: 11,
              color: '#6b7280',
              minWidth: 0
            }}
          >
            <span style={{ flexShrink: 0 }}>设备</span>
            <span
              style={{
                color: '#374151',
                fontWeight: 600,
                minWidth: 0,
                wordBreak: 'break-word',
                whiteSpace: 'normal'
              }}
              title={deviceLabel}
            >
              {deviceLabel}
            </span>
          </div>
        );
      })()}


      <div className="mission-detail-actions" style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        flexWrap: 'wrap',
        borderTop: '1px solid #f3f4f6',
        paddingTop: 10
      }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginRight: 'auto', color: '#6b7280' }}>
          <b style={{ fontWeight: 600 }}>图标</b>
          <span style={{
            width: 18, height: 18, borderRadius: '50%', background: '#d1d5db', display: 'inline-block'
          }}
          />
          <button
            type="button"
            onClick={() => message.info('图标编辑请在全部队员列表展开后修改')}
            style={{
              border: 'none', background: 'transparent', color: '#2563eb',
              cursor: 'pointer', fontSize: 12, padding: 0, fontWeight: 600
            }}
          >
            编辑
          </button>
        </div>

        <button
          type="button"
          style={actionBtn}
          onClick={() => {
            if (onSendCommand) onSendCommand(member);
            else if (onOpenCommandMenu) onOpenCommandMenu(member);
            else message.info('发指令入口稍后接入（当前为占位）');
          }}
          title="向该队员发送任务指令"
        >
          {'\u2708'} 发指令
        </button>
        <button
          type="button"
          style={actionBtn}
          onClick={() => {
            if (member?.last_longitude != null && member?.last_latitude != null) {
              const text = `${Number(member.last_longitude).toFixed(6)},${Number(member.last_latitude).toFixed(6)}`;
              if (onCopyCoords) onCopyCoords(text);
              else if (navigator?.clipboard?.writeText) {
                navigator.clipboard.writeText(text);
                message.success('坐标已复制');
              } else message.info(text);
            } else message.warning('该队员暂无有效坐标');
          }}
          title="复制坐标"
        >
          {'\u2398'} 复制坐标
        </button>

        <div style={{ position: 'relative' }}>
          <button
            type="button"
            style={actionBtn}
            onClick={(e) => {
              e.stopPropagation();
              setMoreOpen(false);
              setTrackOpen((v) => !v);
            }}
            title="轨迹相关操作"
          >
            轨迹 {'\u25BE'}
          </button>
          {trackOpen ? (
            <div
              style={{
                position: 'absolute',
                bottom: '100%',
                left: 0,
                marginBottom: 4,
                minWidth: 148,
                background: '#fff',
                border: '1px solid #e5e7eb',
                borderRadius: 8,
                boxShadow: '0 6px 16px rgba(0,0,0,0.12)',
                padding: 4,
                zIndex: 30
              }}
              role="menu"
            >
              <div style={{ padding: '6px 10px', borderBottom: '1px solid #f3f4f6' }}>
                <TrackSwitch
                  on={!!trackOn}
                  onToggle={(next) => {
                    if (onTrackToggle) onTrackToggle(next);
                    else if (onTrackMenu) onTrackMenu(member);
                  }}
                  title={trackOn ? '关闭轨迹绘制' : '打开轨迹绘制'}
                />
              </div>
              <button
                type="button"
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '7px 10px',
                  fontSize: 12, border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 4
                }}
                onClick={() => {
                  setTrackOpen(false);
                  onLocate?.(member);
                }}
              >
                定位到地图
              </button>
            </div>
          ) : null}
        </div>

        <button
          type="button"
          style={{ ...actionBtn, minWidth: 32, padding: '0 8px' }}
          onClick={() => {
            setTrackOpen(false);
            setMoreOpen((v) => !v);
            if (!moreOpen) message.info('更多队员操作后续补充');
          }}
          title="更多"
        >
          ···
        </button>
      </div>
    </div>
  );
}


/**
 * 任务分组内队员行：两行收起态（与全部列表共用 MemberCollapsedCard）。
 */
function GroupMemberRow({
  member,
  status = PRESENCE_STATUS.OFFLINE,
  expanded = false,
  onToggleExpand,
  draggable = true,
  onDragStart,
  onRemove,
  showVoice = false,
  voicePulsing = false,
  onVoiceClick,
  isStreaming = false,
  onOpenLiveStream = null,
  onFlyTo = null,
  notesByKey = null,
  onOpenCommandMenu = null,
  trackOn = false,
  onTrackToggle = null,
  freqBadge = null,
  missionTag = null,
  capability = null,
  onCapabilityChange = null,
  onSendCommand = null,
  onCopyCoords = null
}) {
  const name = getMemberListDisplayName(member, notesByKey) || memberIdOf(member) || '未知';
  const isOffline = status === PRESENCE_STATUS.OFFLINE || status === 'offline';
  const batteryText = member?.battery_level != null ? `电量 ${member.battery_level}%` : null;
  const seenText = formatMemberSeen(member?.last_seen, isOffline);
  const accuracyText = null;
  const tip = buildMemberInfoLines(member, status, notesByKey).join('\n');
  const statusText = seenText;

  const removeBtn = onRemove ? (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onRemove(); }}
      style={{
        border: 'none',
        background: 'transparent',
        color: '#999',
        cursor: 'pointer',
        fontSize: 14,
        lineHeight: 1,
        padding: 0,
        flexShrink: 0
      }}
      title="移出本组"
    >
      ×
    </button>
  ) : null;

  return (
    <div className={expanded ? 'mission-member-row is-expanded' : 'mission-member-row'} style={{ overflow: 'hidden', position: 'relative' }}>
      <MemberCollapsedCard
        displayName={name}
        presenceStatus={status}
        expanded={expanded}
        onToggleExpand={onToggleExpand}
        nameTitle={onOpenCommandMenu ? (tip + '\n点击打开菜单') : tip}
        onNameClick={onOpenCommandMenu ? ((e) => onOpenCommandMenu(member, e.currentTarget)) : null}
        showVoice={showVoice}
        voiceNode={showVoice ? (
          <VoiceIconButton unread={voicePulsing} onClick={() => onVoiceClick?.(member)} />
        ) : null}
        freqBadge={freqBadge || <FreqStatusBadge member={member} presenceStatus={status} />}
        missionTag={missionTag}
        onLocate={onFlyTo ? (() => onFlyTo(member)) : null}
        isStreaming={isStreaming}
        onOpenLive={onOpenLiveStream ? (() => onOpenLiveStream(member)) : null}
        statusText={statusText}
        batteryText={batteryText}
        accuracyText={accuracyText}
        trackOn={trackOn}
        onTrackToggle={onTrackToggle}
        trackTitle={trackOn ? '关闭该队员轨迹绘制' : '打开该队员轨迹绘制'}
        showCollapsedTrackSwitch={false}
        rightExtra={removeBtn}
        draggable={draggable}
        onDragStart={onDragStart}
      />
      {expanded ? (
        <GroupMemberDetail
          member={member}
          status={status}
          onLocate={onFlyTo}
          onSendCommand={onSendCommand || ((m) => onOpenCommandMenu?.(m))}
          trackOn={trackOn}
          onTrackToggle={onTrackToggle}
          onCopyCoords={onCopyCoords}
          onOpenCommandMenu={onOpenCommandMenu}
          capability={capability}
          onCapabilityChange={onCapabilityChange}
        />
      ) : null}
    </div>
  );
}

/**
 * Mission kanban: pending pool (top) + task groups (bottom).
 * Grouped members stay put when offline; only manual remove / end mission clears them.
 */
export default function MissionKanbanPanel({
  missionName = null,
  missionMembers = [],
  missionGroups = [],
  onGroupsChange,
  onRemoveMember,
  onEndMission,
  endMissionEnabled = null,
  getMemberPresence,
  voicePulseIds = null,
  streamingMembers = null,
  openLiveStream = null,
  getMessagesForMember,
  onOutboundSent,
  onChatOpenChange,
  getMap = null,
  getBasemapId = null,
  onMessageDeleted = null,
  historyEpoch = 0,
  onFlyToMember = null,
  notesByKey = null,
  onSaveMemberNote = null,
  onSendLocationToMember = null,
  onSendMessageToMember = null,
  isDrawing = null,
  startRealTimeTrackDrawing = null,
  stopTrackFeatures = null,
  showTracks = null,
  toggleTrack = null
}) {
  const canEditBoard = canEditMissionBoard();

  const [commandMenu, setCommandMenu] = useState(null);
  const [noteEditor, setNoteEditor] = useState(null);
  const commandMenuRef = useRef(null);
  const closeCommandMenu = () => setCommandMenu(null);
  const openCommandMenuFor = (member, anchorEl) => {
    const key = member?.device_unique_id || member?.name;
    const rect = anchorEl?.getBoundingClientRect?.();
    if (!rect || !key) return;
    setCommandMenu((prev) => {
      if (prev?.key === key) return null;
      const estimatedH = 220;
      const top = rect.bottom + 4 + estimatedH > window.innerHeight
        ? Math.max(8, rect.top - estimatedH - 4)
        : rect.bottom + 4;
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - 140);
      return { key, member, top, left };
    });
  };
  useEffect(() => {
    if (!commandMenu) return undefined;
    const onDoc = (e) => {
      if (commandMenuRef.current && commandMenuRef.current.contains(e.target)) return;
      closeCommandMenu();
    };
    const onKey = (e) => { if (e.key === 'Escape') closeCommandMenu(); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [commandMenu]);
  const [dragPayload, setDragPayload] = useState(null);
  const [editingGroupId, setEditingGroupId] = useState(null);
  const [draftName, setDraftName] = useState('');
  const [poolCollapsed, setPoolCollapsed] = useState(false);
  const [chatMemberId, setChatMemberId] = useState(null);
  // 任务分组内详情展开；默认空 = 上线不自动展开
  const [expandedMemberIds, setExpandedMemberIds] = useState(() => ({}));
  // 任务组内轨迹滑块：默认开；用户关掉后记入 override（false）
  const [trackSwitchOverride, setTrackSwitchOverride] = useState(() => ({}));
  const [capabilityById, setCapabilityById] = useState(() => ({}));
  const autoStartedRef = useRef(new Set());

  const memberTrackOn = (member) => {
    const name = member?.name;
    if (!name) return false;
    // 用户手动拨过以 override 为准；否则任务组内默认开（与 isDrawing 解耦：
    // 绘制由下方 auto-start effect 负责，离线/尚未起画时开关仍应显示开）
    if (Object.prototype.hasOwnProperty.call(trackSwitchOverride, name)) {
      return trackSwitchOverride[name] === true;
    }
    return true;
  };

  const handleMemberTrackToggle = (member, next) => {
    const name = member?.name;
    if (!name) return;
    setTrackSwitchOverride((prev) => ({ ...prev, [name]: !!next }));
    if (next) {
      startRealTimeTrackDrawing?.(name, { auto: false });
      if (showTracks && !showTracks[name] && toggleTrack) {
        // 若已有历史轨迹但未显示，顺带打开显隐（toggle 在无轨迹时会提示，可忽略）
        try { toggleTrack(name); } catch (_) { /* ignore */ }
      }
    } else {
      stopTrackFeatures?.(name);
    }
  };



  const memberById = useMemo(() => {
    const map = {};
    (missionMembers || []).forEach((m) => {
      const id = memberIdOf(m);
      if (id) map[id] = m;
    });
    return map;
  }, [missionMembers]);

  const groupedIds = useMemo(() => {
    const set = new Set();
    (missionGroups || []).forEach((g) => {
      (g.memberIds || []).forEach((id) => set.add(id));
    });
    return set;
  }, [missionGroups]);

  const poolMembers = useMemo(
    () => (missionMembers || []).filter((m) => {
      const id = memberIdOf(m);
      if (!id) return false;
      if (groupedIds.has(id)) return false;
      // fuzzy: group may store bind-token id while live row is device id
      for (const gid of groupedIds) {
        if (missionIdMatchesMember(gid, m)) return false;
      }
      return true;
    }),
    [missionMembers, groupedIds]
  );

  const resolveMember = (id) => {
    if (!id) return null;
    const accept = (m) => {
      if (!m) return null;
      // Stub：仅藏长 hex 令牌幽灵；真人名册行一律保留（含 leidian14A 等设备名）
      if (m.__missionStub) {
        if (looksLikeGhostMissionId(id) || looksLikeGhostMissionId(m.device_unique_id)) {
          return null;
        }
        return m;
      }
      // 名册里的行 = 可拖可编组；不再用 memberHasHumanDisplayName 过滤（否则拖入分组后消失）
      return m;
    };
    if (memberById[id]) return accept(memberById[id]);
    const fuzzy = (missionMembers || []).find((m) => missionIdMatchesMember(id, m));
    if (fuzzy) return accept(fuzzy);
    // Never promote raw bind/stream tokens into selectable/display rows
    if (looksLikeGhostMissionId(id)) return null;
    return accept(stubMemberFromId(id));
  };

  /** 任务组内多个历史 id（名字/uid/zhxt_app）可能指向同一人：渲染前按 live key 去重 */
  const resolveGroupMembers = (ids) => {
    const out = [];
    const seen = new Set();
    (ids || []).forEach((storedId) => {
      const m = resolveMember(storedId);
      if (!m) return;
      const liveKey = memberIdOf(m) || String(storedId || '').trim();
      if (!liveKey || seen.has(liveKey)) return;
      seen.add(liveKey);
      out.push({ member: m, storedId: liveKey });
    });
    return out;
  };

  // 进入任务组的队员：默认自动开绘制（用户关掉的不再强开）
  useEffect(() => {
    const names = [];
    (missionGroups || []).forEach((g) => {
      resolveGroupMembers(g.memberIds || []).forEach(({ member }) => {
        if (member?.name) names.push(member.name);
      });
    });
    names.forEach((name) => {
      if (trackSwitchOverride[name] === false) return;
      if (typeof isDrawing === 'function' && isDrawing(name)) {
        autoStartedRef.current.add(name);
        return;
      }
      if (autoStartedRef.current.has(name)) return;
      autoStartedRef.current.add(name);
      startRealTimeTrackDrawing?.(name, { auto: true });
    });
  }, [missionGroups, missionMembers, trackSwitchOverride, isDrawing, startRealTimeTrackDrawing]);


  const setGroups = (next) => {
    onGroupsChange?.(typeof next === 'function' ? next(missionGroups || []) : next);
  };

  const beginDrag = (memberId, from) => (e) => {
    setDragPayload({ memberId, from });
    try {
      e.dataTransfer.setData('text/plain', JSON.stringify({ memberId, from }));
      e.dataTransfer.effectAllowed = 'move';
      const member = resolveMember(memberId);
      const label = getMemberDisplayName(member) || memberIdOf(member) || String(memberId || '队员');
      const status = presenceOf(member, getMemberPresence);
      attachMissionNameDragImage(e.dataTransfer, label, status);
    } catch (_) { /* ignore */ }
  };

  const readDrag = (e) => {
    try {
      const raw = e.dataTransfer.getData('text/plain');
      if (raw) return JSON.parse(raw);
    } catch (_) { /* ignore */ }
    return dragPayload;
  };

  const moveMember = (memberId, to) => {
    if (!memberId) return;
    setGroups((prev) => {
      const cleaned = (prev || []).map((g) => ({
        ...g,
        memberIds: (g.memberIds || []).filter((id) => !(
          id === memberId
          || missionIdMatchesMember(memberId, { device_unique_id: id, name: id })
          || missionIdMatchesMember(id, { device_unique_id: memberId, name: memberId })
        ))
      }));
      if (to === 'pool') return cleaned;
      return cleaned.map((g) => {
        if (g.id !== to) return g;
        const ids = g.memberIds || [];
        const live = resolveMember(memberId);
        const liveKey = memberIdOf(live) || memberId;
        const stub = live || { device_unique_id: memberId, name: memberId };
        if (ids.some((id) => id === liveKey || id === memberId || missionIdMatchesMember(id, stub))) {
          return {
            ...g,
            memberIds: ids.map((id) => (
              id === memberId || id === liveKey || missionIdMatchesMember(id, stub) ? liveKey : id
            ))
          };
        }
        return { ...g, memberIds: [...ids, liveKey] };
      });
    });
  };

  const onDropTo = (to) => (e) => {
    e.preventDefault();
    e.stopPropagation();
    const payload = readDrag(e);
    if (!payload?.memberId) return;
    moveMember(payload.memberId, to);
    setDragPayload(null);
  };

  const allowDrop = (e) => {
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch (_) { /* ignore */ }
  };

  const addGroup = () => {
    const n = (missionGroups || []).length + 1;
    const id = 'g_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 6);
    setGroups((prev) => [
      ...(prev || []),
      { id, name: '临时组 ' + n, collapsed: false, memberIds: [], color: COLORS[(n - 1) % COLORS.length], commsLinked: false, recordTracks: true }
    ]);
  };

  const renameGroup = (id) => {
    const name = String(draftName || '').trim();
    if (!name) {
      setEditingGroupId(null);
      return;
    }
    setGroups((prev) => (prev || []).map((g) => (g.id === id ? { ...g, name } : g)));
    setEditingGroupId(null);
  };

  const removeGroup = (id) => {
    setGroups((prev) => (prev || []).filter((g) => g.id !== id));
  };

  const toggleCollapse = (id) => {
    setGroups((prev) => (prev || []).map((g) => (g.id === id ? { ...g, collapsed: !g.collapsed } : g)));
  };

  const toggleCommsLinked = (id) => {
    setGroups((prev) => (prev || []).map((g) => (
      g.id === id ? { ...g, commsLinked: g.commsLinked !== true } : g
    )));
  };

  const toggleRecordTracks = (id) => {
    setGroups((prev) => (prev || []).map((g) => (
      g.id === id ? { ...g, recordTracks: g.recordTracks !== true } : g
    )));
  };

  const total = (missionMembers || []).length;
  const pulseSet = voicePulseIds instanceof Set
    ? voicePulseIds
    : new Set(Array.isArray(voicePulseIds) ? voicePulseIds : Object.keys(voicePulseIds || {}));

  const isPulsing = (m) => {
    const id = memberIdOf(m);
    if (!id) return false;
    if (pulseSet.has(id)) return true;
    if (m?.device_unique_id && pulseSet.has(m.device_unique_id)) return true;
    if (m?.name && pulseSet.has(m.name)) return true;
    return false;
  };
  const isMemberStreaming = (m) => {
    if (!m?.name || !streamingMembers?.has) return false;
    if (streamingMembers.has(m.name)) return true;
    // fallback: display-name key if ever stored differently
    try {
      const dn = getMemberDisplayName(m);
      return !!(dn && streamingMembers.has(dn));
    } catch (_) {
      return false;
    }
  };


  useEffect(() => {
    const member = chatMemberId ? resolveMember(chatMemberId) : null;
    onChatOpenChange?.(member || null);
    return () => { onChatOpenChange?.(null); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chatMemberId, onChatOpenChange]);

  const panelBox = {
    background: '#f7f9fc',
    border: '1px solid #e6ebf0',
    borderRadius: 8,
    padding: 8,
    display: 'flex',
    flexDirection: 'column',
    minWidth: 0
  };

  const chatMember = chatMemberId ? resolveMember(chatMemberId) : null;

  const commandMenuPortal = commandMenu && typeof document !== 'undefined'
    ? ReactDOM.createPortal(
      (
        <div
          ref={commandMenuRef}
          role="menu"
          style={{
            position: 'fixed',
            top: commandMenu.top,
            left: commandMenu.left,
            zIndex: 10050,
            minWidth: 120,
            background: '#fff',
            border: '1px solid #d0d7de',
            borderRadius: 8,
            boxShadow: '0 8px 24px rgba(15,23,42,.18)',
            padding: 6
          }}
        >
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              onSendLocationToMember?.(m);
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              background: '#f8fafc', padding: '8px 10px', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, color: '#0f172a', marginBottom: 4
            }}
          >
            发送位置
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              onSendMessageToMember?.(m);
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              background: '#f8fafc', padding: '8px 10px', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, color: '#0f172a', marginBottom: 4
            }}
          >
            发送消息
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              onFlyToMember?.(m);
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              background: '#f8fafc', padding: '8px 10px', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, color: '#0f172a', marginBottom: 4
            }}
          >
            定位到地图
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              const m = commandMenu.member;
              closeCommandMenu();
              setNoteEditor({ member: m, focusSection: 'remark' });
            }}
            style={{
              display: 'block', width: '100%', textAlign: 'left', border: 'none',
              background: '#f8fafc', padding: '8px 10px', borderRadius: 6,
              cursor: 'pointer', fontSize: 13, color: '#0f172a'
            }}
          >
            备注
          </button>
        </div>
      ),
      document.body
    )
    : null;

  return (
    <>
    <div className="mission-board" style={{
      marginTop: 8,
      width: '100%',
      background: '#fff',
      border: '1px solid #e0e0e0',
      borderRadius: 10,
      padding: 12
    }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'stretch', minHeight: 200 }}>
        {/* Pending pool */}
        {poolMembers.length > 0 ? (
        <div
          className="mission-pending-pool"
          onDragOver={allowDrop}
          onDrop={onDropTo('pool')}
          style={{
            ...panelBox,
            width: '100%',
            maxHeight: poolCollapsed ? undefined : 180,
            borderStyle: 'dashed',
            borderColor: '#c5d0dc'
          }}
        >
          <button
            type="button"
            onClick={() => setPoolCollapsed((v) => !v)}
            title="点击收起/展开待编组"
            style={{
              fontSize: 12,
              fontWeight: 600,
              color: '#555',
              marginBottom: 0,
              border: 'none',
              background: 'transparent',
              cursor: 'pointer',
              padding: 0,
              textAlign: 'left',
              display: 'flex',
              alignItems: 'center',
              gap: 6
            }}
          >
            <span style={{ width: 14, color: '#666' }}>{poolCollapsed ? '▸' : '▾'}</span>
            待编组 · {poolMembers.length}
          </button>
          {!poolCollapsed && (
            <div style={{ flex: 1, overflowY: 'auto' }}>
              {poolMembers.length === 0 ? (
                <div style={{ fontSize: 11, color: '#8a93a0', padding: '12px 4px', lineHeight: 1.5 }}>
                  从「全部」勾选队员后点「加入任务中」
                </div>
              ) : (
                <div className="mission-group-members" style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                  {poolMembers.map((m) => {
                    const id = memberIdOf(m);
                    const rowKey = id || m.name;
                    const isExpanded = !!expandedMemberIds[rowKey];
                    // 待编组：轨迹滑块默认关（仅用户手动开 / override true）
                    const poolTrackOn = trackSwitchOverride[m?.name] === true
                      || (typeof isDrawing === 'function' && isDrawing(m?.name));
                    return (
                      <GroupMemberRow
                        key={id}
                        member={m}
                        status={presenceOf(m, getMemberPresence)}
                        expanded={isExpanded}
                        onToggleExpand={() => setExpandedMemberIds((prev) => ({
                          ...prev,
                          [rowKey]: !prev[rowKey]
                        }))}
                        showVoice
                        voicePulsing={isPulsing(m)}
                        onVoiceClick={() => setChatMemberId((prev) => (prev === id ? null : id))}
                        isStreaming={isMemberStreaming(m)}
                        onOpenLiveStream={openLiveStream}
                        draggable={canEditBoard}
                        onDragStart={beginDrag(id, 'pool')}
                        onRemove={canEditBoard ? () => onRemoveMember?.(id) : undefined}
                        onFlyTo={onFlyToMember}
                        notesByKey={notesByKey}
                        onOpenCommandMenu={openCommandMenuFor}
                        trackOn={poolTrackOn}
                        onTrackToggle={(next) => handleMemberTrackToggle(m, next)}
                        freqBadge={<FreqStatusBadge member={m} presenceStatus={presenceOf(m, getMemberPresence)} />}
                        missionTag={null}
                        capability={capabilityById[rowKey] || null}
                        onCapabilityChange={(cap) => setCapabilityById((prev) => ({ ...prev, [rowKey]: cap }))}
                        onSendCommand={(mem) => onSendMessageToMember?.(mem)}
                      />
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
        ) : null}

        {/* Task groups */}
        <div className="mission-groups" style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#555', marginRight: 'auto' }}>任务分组</div>
            <button
              type="button"
              onClick={addGroup}
              style={{
                height: 26,
                padding: '0 10px',
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #1976d2',
                background: '#fff',
                color: '#1976d2',
                cursor: 'pointer'
              }}
            >
              + 新建分组
            </button>
            <button
              type="button"
              onClick={() => {
                if (!window.confirm('确认结束该任务？结束后将清除待编组与任务分组，收回语音权限并清理语音缓存；队员轨迹将保留。')) return;
                onEndMission?.();
              }}
              disabled={endMissionEnabled == null ? total === 0 : !endMissionEnabled}
              style={{
                height: 26,
                padding: '0 10px',
                fontSize: 12,
                borderRadius: 6,
                border: '1px solid #d0d7de',
                background: (endMissionEnabled == null ? total === 0 : !endMissionEnabled) ? '#f5f7fa' : '#fff',
                color: (endMissionEnabled == null ? total === 0 : !endMissionEnabled) ? '#aaa' : '#c62828',
                cursor: (endMissionEnabled == null ? total === 0 : !endMissionEnabled) ? 'not-allowed' : 'pointer'
              }}
              title="清空待编组与任务分组，收回语音权限，清理语音缓存；保留队员轨迹"
            >
              结束任务
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 4 }}>
            {(missionGroups || []).length === 0 ? (
              <div style={{ fontSize: 11, color: '#8a93a0', padding: 8 }}>
                尚未建组。可先把人留在待编组，或新建后拖入。
              </div>
            ) : (
              (missionGroups || []).map((g) => {
                const color = g.color || COLORS[0];
                const ids = g.memberIds || [];
                const resolved = resolveGroupMembers(ids);
                const members = resolved.map((x) => x.member);
                return (
                  <div
                    key={g.id}
                    className="mission-group"
                    onDragOver={allowDrop}
                    onDrop={onDropTo(g.id)}
                    style={{
                      ...panelBox,
                      borderLeft: '3px solid ' + color,
                      background: '#fafbfd',
                      position: 'relative'
                    }}
                  >
                    <div className="mission-group-heading" style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 0 }}>
                      <button
                        type="button"
                        onClick={() => toggleCollapse(g.id)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: 0,
                          width: 16,
                          color: '#666',
                          fontSize: 12
                        }}
                      >
                        {g.collapsed ? '▸' : '▾'}
                      </button>
                      {editingGroupId === g.id ? (
                        <input
                          autoFocus
                          value={draftName}
                          onChange={(e) => setDraftName(e.target.value)}
                          onBlur={() => renameGroup(g.id)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') renameGroup(g.id);
                            if (e.key === 'Escape') setEditingGroupId(null);
                          }}
                          style={{
                            flex: 1,
                            height: 24,
                            fontSize: 12,
                            border: '1px solid #1976d2',
                            borderRadius: 4,
                            padding: '0 6px',
                            minWidth: 0
                          }}
                        />
                      ) : (
                        <button
                          type="button"
                          onClick={() => { setEditingGroupId(g.id); setDraftName(g.name || ''); }}
                          style={{
                            flex: 1,
                            textAlign: 'left',
                            border: 'none',
                            background: 'transparent',
                            cursor: 'pointer',
                            fontSize: 12,
                            fontWeight: 600,
                            color: '#333',
                            padding: 0,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap'
                          }}
                          title="点击改名"
                        >
                          {g.name || '未知'} · {members.length}
                        </button>
                      )}
                      <button
                        type="button"
                        role="switch"
                        aria-checked={g.commsLinked === true}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleCommsLinked(g.id);
                        }}
                        title="开启后本组成员加入本任务群聊"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          flexShrink: 0,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: '0 2px',
                          fontSize: 11,
                          color: g.commsLinked === true ? '#1976d2' : '#8a93a0',
                          fontWeight: g.commsLinked === true ? 600 : 400,
                          lineHeight: 1
                        }}
                      >
                        <span>通联</span>
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'relative',
                            width: 28,
                            height: 16,
                            borderRadius: 8,
                            background: g.commsLinked === true ? '#1976d2' : '#cfd5de',
                            transition: 'background 0.15s ease',
                            flexShrink: 0
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              top: 2,
                              left: g.commsLinked === true ? 14 : 2,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              background: '#fff',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                              transition: 'left 0.15s ease'
                            }}
                          />
                        </span>
                      </button>
                      <button
                        type="button"
                        role="switch"
                        aria-checked={g.recordTracks === true}
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          toggleRecordTracks(g.id);
                        }}
                        title="开启后本组自动记录轨迹并续画（大空隙分段不硬连）"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 4,
                          flexShrink: 0,
                          border: 'none',
                          background: 'transparent',
                          cursor: 'pointer',
                          padding: '0 2px',
                          fontSize: 11,
                          color: g.recordTracks === true ? '#2e7d32' : '#8a93a0',
                          fontWeight: g.recordTracks === true ? 600 : 400,
                          lineHeight: 1
                        }}
                      >
                        <span>轨迹</span>
                        <span
                          aria-hidden="true"
                          style={{
                            position: 'relative',
                            width: 28,
                            height: 16,
                            borderRadius: 8,
                            background: g.recordTracks === true ? '#2e7d32' : '#cfd5de',
                            transition: 'background 0.15s ease',
                            flexShrink: 0
                          }}
                        >
                          <span
                            style={{
                              position: 'absolute',
                              top: 2,
                              left: g.recordTracks === true ? 14 : 2,
                              width: 12,
                              height: 12,
                              borderRadius: '50%',
                              background: '#fff',
                              boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
                              transition: 'left 0.15s ease'
                            }}
                          />
                        </span>
                      </button>
                      <button
                        type="button"
                        onClick={() => removeGroup(g.id)}
                        style={{
                          border: 'none',
                          background: 'transparent',
                          color: '#999',
                          cursor: 'pointer',
                          fontSize: 12,
                          padding: 0,
                          flexShrink: 0
                        }}
                        title="删除分组（成员回待编组）"
                      >
                        删组
                      </button>
                    </div>
                    {!g.collapsed && (
                      <div className="mission-group-members" style={{ display: 'flex', flexDirection: 'column', gap: 0, position: 'relative' }}>
                        {members.length === 0 ? (
                          <div style={{ fontSize: 11, color: '#8a93a0', padding: '4px 2px' }}>拖入队员</div>
                        ) : (
                          resolved.map(({ member: m, storedId }) => {
                            const id = memberIdOf(m) || storedId;
                            const rowKey = storedId || id;
                            const isExpanded = !!expandedMemberIds[rowKey];
                            return (
                              <div key={rowKey} style={{ position: 'relative' }}>
                                <GroupMemberRow
                                  member={m}
                                  status={presenceOf(m, getMemberPresence)}
                                  expanded={isExpanded}
                                  onToggleExpand={() => setExpandedMemberIds((prev) => ({
                                    ...prev,
                                    [rowKey]: !prev[rowKey]
                                  }))}
                                  showVoice
                                  voicePulsing={isPulsing(m)}
                                  onVoiceClick={() => setChatMemberId((prev) => (prev === id ? null : id))}
                                  isStreaming={isMemberStreaming(m)}
                                  onOpenLiveStream={openLiveStream}
                                  draggable={canEditBoard}
                        onDragStart={beginDrag(storedId || id, g.id)}
                                  onRemove={() => moveMember(storedId || id, 'pool')}
                                  onFlyTo={onFlyToMember}
                                  notesByKey={notesByKey}
                                  onOpenCommandMenu={openCommandMenuFor}
                                  trackOn={memberTrackOn(m)}
                                  onTrackToggle={(next) => handleMemberTrackToggle(m, next)}
                                  freqBadge={<FreqStatusBadge member={m} presenceStatus={presenceOf(m, getMemberPresence)} />}
                                  missionTag={null}
                                  capability={capabilityById[rowKey] || null}
                                  onCapabilityChange={(next) => setCapabilityById((prev) => ({ ...prev, [rowKey]: next }))}
                                  onSendCommand={(mem) => {
                                    openCommandMenuFor(mem, null);
                                    // 无锚点时仍尽量打开发指令相关入口
                                    onSendMessageToMember?.(mem);
                                  }}
                                />
                              </div>
                            );
                          })
                        )}
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
      {chatMember ? (
        <MissionMemberChatWindow
          member={chatMember}
          messages={getMessagesForMember?.(chatMember) || []}
          onClose={() => setChatMemberId(null)}
          onOutboundSent={(entry) => { onOutboundSent?.(entry); }}
          onMessageDeleted={(item) => { onMessageDeleted?.(item); }}
          getMap={getMap}
          getBasemapId={getBasemapId}
          historyEpoch={historyEpoch}
          isStreaming={isMemberStreaming(chatMember)}
          onOpenLiveStream={openLiveStream}
        />
      ) : null}
    </div>
      <div
        style={{
          marginTop: 8,
          padding: '8px 10px',
          background: '#f3f4f6',
          borderRadius: 8,
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          fontSize: 11,
          color: '#6b7280'
        }}
      >
        <span
          aria-hidden
          style={{
            width: 16,
            height: 16,
            borderRadius: '50%',
            border: '1px solid #9ca3af',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 10,
            fontWeight: 700,
            color: '#6b7280',
            flexShrink: 0
          }}
        >
          i
        </span>
        <span>任务结束后恢复原上报策略</span>
      </div>
      {commandMenuPortal}
      <MemberNoteEditorModal
        open={!!noteEditor}
        member={noteEditor?.member}
        note={
          noteEditor?.member
            ? (notesByKey && notesByKey[memberNoteKey(noteEditor.member)]) || null
            : null
        }
        focusSection={noteEditor?.focusSection || 'remark'}
        onClose={() => setNoteEditor(null)}
        onSave={onSaveMemberNote}
      />
    </>
  );
}
